import type { Stack, StackType } from "../../shared/stack.js";
import { normalizeStack, stackKey } from "../../shared/stack.js";
import type { Snapshot } from "../../shared/api.js";
import type { Topic } from "../LiveStore.js";
import type { AppContext } from "../Page.js";
import { clear, debounce, h } from "../dom.js";
import { ago, amount, stackDetail, stackName } from "../format.js";
import { stackIcon } from "./StackIcon.js";

interface Loading {
  startedAt: number;
  received: number;
  /** 0 to 1, from a total or from the game's own progress. Null when unknown. */
  share: number | null;
  total: number | null;
}

/** Where the picker's search results come from. */
interface PickerSource {
  topic: Topic;
  placeholder: string;
  reading: string;
  info(snap: Snapshot | null): string;
  loading(snap: Snapshot | null): Loading | null;
  search(ctx: AppContext, q: string, type: string | undefined): Promise<{ items: Stack[]; hints?: (string | null)[]; amounts?: number[]; matches: number }>;
  refresh(ctx: AppContext): Promise<string>;
  /** Read from the game straight away when nothing has been read yet. */
  autoRead?: boolean;
}

const SOURCES: Record<"craftables" | "network", PickerSource> = {
  craftables: {
    topic: "craftables",
    placeholder: "Search craftables (label, name, ore dictionary, e.g. hv circuit)…",
    reading: "Reading craftables from the game… ",
    info(snap) {
      const count = snap?.craftablesCount ?? 0;
      let text = count
        ? `${count.toLocaleString()} craftables, loaded ${ago(snap?.craftablesAt ?? null)}`
        : "Craftables not loaded yet. Reload them from the game, or enter a stack by hand.";
      // Without ore names, "circuit" or "hv circuit" can't find circuits whose label doesn't say so.
      if (count && !snap?.craftablesWithOre) {
        text += ". No ore dictionary names came with them, so searching by ore name or circuit tier won't work: update the Butler and reload.";
      }
      return text;
    },
    loading(snap) {
      const l = snap?.craftablesLoading;
      return l ? { startedAt: l.startedAt, received: l.received, total: l.total, share: l.total ? l.received / l.total : null } : null;
    },
    search: (ctx, q, type) => ctx.api.craftables(q, type, 50),
    refresh: async (ctx) => `Loaded ${(await ctx.api.refreshCraftables()).count} craftables`,
  },
  network: {
    topic: "network",
    placeholder: "Search what the network stores (label, name, ore dictionary)…",
    reading: "Reading the network's contents from the game… ",
    info(snap) {
      const n = snap?.network;
      return n?.count
        ? `${n.count.toLocaleString()} stored stacks, read ${ago(n.at)}`
        : "The network's contents have not been read yet. Read them from the game, or enter a stack by hand.";
    },
    loading(snap) {
      const l = snap?.network?.loading;
      return l ? { startedAt: l.startedAt, received: l.received, total: null, share: l.progress } : null;
    },
    search: (ctx, q, type) => ctx.api.network(q, type, 50),
    refresh: async (ctx) => `Read ${(await ctx.api.refreshNetwork()).count} stored stacks`,
    autoRead: true,
  },
};

/**
 * Picks a stack: search the craftables the game reported (or, with the
 * "network" source, everything the network stores), or type one in by hand
 * (useful before the list has been loaded).
 */
export class StackPicker {
  readonly el: HTMLElement;
  private selected: Stack | null = null;
  private readonly results = h("ul", { class: "picker-results" });
  private readonly source: PickerSource;
  private readonly search: HTMLInputElement;
  private readonly typeFilter = h(
    "select",
    null,
    h("option", { value: "" }, "All types"),
    h("option", { value: "item" }, "Items"),
    h("option", { value: "fluid" }, "Fluids"),
    h("option", { value: "essentia" }, "Essentia"),
  );
  private readonly chosen = h("div", { class: "picker-chosen muted" }, "Nothing selected");
  private readonly info = h("div", { class: "muted small" });
  private readonly progress = h("div", { class: "picker-loading", hidden: true });
  private readonly refreshButton: HTMLButtonElement;
  private ticker: number | undefined;
  private readonly iconHint = h("div", { class: "muted small", hidden: true }, "No item pictures yet: import them from NEI (README, \"Item images\").");
  private readonly manualType = h("select", null, ...(["item", "fluid", "essentia"] as StackType[]).map((t) => h("option", { value: t }, t)));
  private readonly manualName = h("input", { placeholder: "minecraft:iron_ingot, water or ignis" });
  private readonly manualDamage = h("input", { type: "number", min: "0", value: "0", class: "narrow" });
  private readonly manualLabel = h("input", { placeholder: "Label (optional)" });

  constructor(
    private readonly ctx: AppContext,
    private readonly onChange: (stack: Stack | null) => void = () => undefined,
    source: keyof typeof SOURCES = "craftables",
  ) {
    this.source = SOURCES[source];
    this.search = h("input", { type: "search", placeholder: this.source.placeholder, autocomplete: "off" });
    const run = debounce(() => void this.runSearch(), 200);
    this.search.addEventListener("input", run);
    this.typeFilter.addEventListener("change", run);
    const manualChanged = () => {
      this.manualDamage.disabled = this.manualType.value !== "item";
      this.select(
        normalizeStack({
          type: this.manualType.value,
          name: this.manualName.value,
          damage: this.manualDamage.value,
          label: this.manualLabel.value,
        }),
      );
    };
    for (const input of [this.manualType, this.manualName, this.manualDamage, this.manualLabel]) {
      input.addEventListener("input", manualChanged);
    }
    const refresh = h("button", { type: "button", class: "link", onclick: () => void this.refresh() }, this.reloadLabel);
    this.refreshButton = refresh;
    this.el = h(
      "div",
      { class: "picker" },
      h("div", { class: "row" }, this.search, this.typeFilter),
      h("div", { class: "row small" }, this.info, refresh),
      this.progress,
      this.iconHint,
      this.results,
      h(
        "details",
        { class: "manual" },
        h("summary", null, "Enter a stack by hand"),
        h(
          "div",
          { class: "row" },
          h("label", null, "Type", this.manualType),
          h("label", { class: "grow" }, "Name", this.manualName),
          h("label", null, "Damage", this.manualDamage),
        ),
        h("label", null, "Label", this.manualLabel),
      ),
      this.chosen,
    );
    // A fetch started elsewhere (another tab, another picker) shows here too.
    const off = this.ctx.live.on(this.source.topic, () => {
      if (!this.el.isConnected) {
        off();
        this.stopTicker();
        return;
      }
      const wasLoading = this.ticker !== undefined;
      this.updateInfo();
      if (wasLoading && this.ticker === undefined) void this.runSearch();
    });
    this.updateInfo();
    void this.runSearch();
    const snap = ctx.live.snapshot;
    if (this.source.autoRead && snap?.link.connected && !this.source.loading(snap) && !snap.network?.count) void this.refresh();
    void ctx.api.icons().then(
      ({ installed }) => (this.iconHint.hidden = installed),
      () => undefined,
    );
  }

  get value(): Stack | null {
    return this.selected;
  }

  focus(): void {
    this.search.focus();
  }

  private select(stack: Stack | null): void {
    this.selected = stack;
    clear(this.chosen);
    if (stack) {
      this.chosen.className = "picker-chosen";
      this.chosen.append(stackIcon(stack, "lg"), h("strong", null, stackName(stack)), " ", h("span", { class: "muted" }, stackDetail(stack)));
    } else {
      this.chosen.className = "picker-chosen muted";
      this.chosen.textContent = "Nothing selected";
    }
    this.onChange(stack);
  }

  private get reloadLabel(): string {
    return this.source.topic === "network" ? "Read from game" : "Reload from game";
  }

  private updateInfo(): void {
    this.info.textContent = this.source.info(this.ctx.live.snapshot);
    this.renderProgress();
  }

  /** Shows a running fetch: what is happening, how far it got and for how long. */
  private renderProgress(): void {
    const loading = this.source.loading(this.ctx.live.snapshot);
    this.refreshButton.disabled = loading !== null;
    this.refreshButton.textContent = loading ? "Loading…" : this.reloadLabel;
    clear(this.progress);
    this.progress.hidden = loading === null;
    if (!loading) {
      this.stopTicker();
      return;
    }
    if (this.ticker === undefined) this.ticker = window.setInterval(() => this.renderProgress(), 1000);
    const seconds = Math.max(0, Math.round((Date.now() - loading.startedAt) / 1000));
    const pct = loading.share === null ? null : Math.min(100, loading.share * 100);
    const bar = h("div", { class: `bar${pct === null ? " indeterminate" : ""}` }, h("span", { style: pct === null ? "" : `width: ${pct.toFixed(1)}%` }));
    const counted = loading.total
      ? `${loading.received.toLocaleString()} of ${loading.total.toLocaleString()} received`
      : loading.received
        ? `${loading.received.toLocaleString()} received`
        : "Waiting for the first ones";
    this.progress.append(
      h("div", { class: "small" }, h("strong", null, this.source.reading), `${counted}, ${seconds} s`),
      bar,
      h("div", { class: "muted small" }, "The computer reads the ME network between its other work. You can keep using the dashboard or enter a stack by hand meanwhile."),
    );
  }

  private stopTicker(): void {
    window.clearInterval(this.ticker);
    this.ticker = undefined;
  }

  private async refresh(): Promise<void> {
    this.refreshButton.disabled = true;
    try {
      this.ctx.toast(await this.source.refresh(this.ctx));
      await this.runSearch();
    } catch (err) {
      this.ctx.toast((err as Error).message, "error");
    } finally {
      this.updateInfo();
    }
  }

  private async runSearch(): Promise<void> {
    try {
      const { items, hints, amounts, matches } = await this.source.search(this.ctx, this.search.value, this.typeFilter.value || undefined);
      clear(this.results);
      items.forEach((stack, i) => {
        const hint = hints?.[i];
        const li = h(
          "li",
          {
            tabindex: "0",
            class: this.selected && stackKey(this.selected) === stackKey(stack) ? "active" : "",
            onclick: () => {
              for (const el of this.results.querySelectorAll("li.active")) el.classList.remove("active");
              li.classList.add("active");
              this.select(stack);
            },
            onkeydown: (e: KeyboardEvent) => e.key === "Enter" && li.click(),
          },
          stackIcon(stack, "lg"),
          h("span", null, stackName(stack)),
          hint ? h("span", { class: "badge" }, hint) : null,
          amounts ? h("span", { class: "small" }, amount(amounts[i], stack.type)) : null,
          h("span", { class: "muted small" }, stackDetail(stack)),
        );
        this.results.appendChild(li);
      });
      if (!items.length && this.search.value) this.results.appendChild(h("li", { class: "muted empty" }, "No match"));
      if (matches > items.length) {
        this.results.appendChild(
          h("li", { class: "muted empty" }, `${items.length} of ${matches.toLocaleString()} matches shown. Add a word to narrow it down.`),
        );
      }
    } catch (err) {
      this.ctx.toast((err as Error).message, "error");
    }
  }
}
